import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Suspense, lazy } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AdminLayout } from "@/components/AdminLayout";
import { queryClient } from "@/lib/queryClient";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";

// Lazy imports for code-splitting
const Landing = lazy(() => import("./pages/Landing"));
const Questionnaire = lazy(() => import("./pages/Questionnaire"));
const Results = lazy(() => import("./pages/Results"));
const Liked = lazy(() => import("./pages/Liked"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Magazin = lazy(() => import("./pages/Magazin"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const SEOLandingPage = lazy(() => import("./pages/SEOLandingPage"));
const GeschenkideenHub = lazy(() => import("./pages/GeschenkideenHub"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminTrendFinder = lazy(() => import("./pages/admin/TrendFinder"));
const AdminSEOAnalyzer = lazy(() => import("./pages/admin/SEOAnalyzer"));
const AdminArticleGenerator = lazy(() => import("./pages/admin/ArticleGenerator"));
const AdminCronJobs = lazy(() => import("./pages/admin/CronJobs"));
const AdminArticleBackups = lazy(() => import("./pages/admin/ArticleBackups"));
const AdminAPITester = lazy(() => import("./pages/admin/APITester"));
const AdminArticleOptimizer = lazy(() => import("./pages/admin/ArticleOptimizer"));
const AdminSEOPages = lazy(() => import("./pages/admin/SEOPages"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));

const App = () => {
  usePerformanceMonitor(true);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <div className="overflow-x-hidden w-full">
            <ScrollToTop />
            <Header />
            <Suspense fallback={null}>
              <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/questionnaire" element={<Questionnaire />} />
              <Route path="/results" element={<Results />} />
              <Route path="/liked" element={<Liked />} />
              <Route path="/magazin" element={<Magazin />} />
              <Route path="/magazin/:slug" element={<ArticlePage />} />
              
              {/* Geschenkideen Hub Page */}
              <Route path="/geschenkideen" element={<GeschenkideenHub />} />
              
              {/* Programmatic SEO Landing Pages - Dynamic catch-all for generated slugs */}
              <Route path="/geschenkideen/*" element={<SEOLandingPage />} />
              <Route path="/geschenke-fuer-:slug" element={<SEOLandingPage />} />
              <Route path="/geschenke-:slug" element={<SEOLandingPage />} />
              {/* Dynamic SEO pages at root level (e.g., geburtstagsgeschenke-fuer-mama-25-50-euro) */}
              <Route path="/:slug" element={<SEOLandingPage />} />
              
              <Route path="/admin/login" element={<AdminLogin />} />
              
              {/* Admin Routes with Layout */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="trends" element={<AdminTrendFinder />} />
                <Route path="seo" element={<AdminSEOAnalyzer />} />
                <Route path="seo-pages" element={<AdminSEOPages />} />
                <Route path="generator" element={<AdminArticleGenerator />} />
                <Route path="jobs" element={<AdminCronJobs />} />
                <Route path="backups" element={<AdminArticleBackups />} />
                <Route path="apis" element={<AdminAPITester />} />
                <Route path="optimizer" element={<AdminArticleOptimizer />} />
              </Route>
              
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/ueber-uns" element={<UeberUns />} />
              <Route path="/kontakt" element={<Kontakt />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer />
          </div>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
